import { updatePassword, updateUserAttribute } from 'aws-amplify/auth'
import {
  adminUpdateUserSettings,
  deleteMyAccount as deleteMyAccountApi,
  fetchMe,
  getAttachmentDownloadUrl,
  getAttachmentUploadUrl,
  updateMeProfile,
  type UserProfile,
} from './appsync-client'
import { uploadFileToS3 } from './media-upload-service'
import { setUserActivityStatus } from '../stores/presence-store'
import type { ProviderActivityStatus } from '@shared/provider-activity/types'
import type { OnCallOrgConfig, OnCallPeriod } from '@shared/on-call/types'
import {
  DAYTIME_BOOKINGS_KEY,
  DAYTIME_ORG_CONFIG_KEY,
  type DaytimeContactPeriod,
  type DaytimeOrgConfig,
} from '@shared/daytime-contact/types'
import type {
  AccessibilityPresetId,
  AccessibilitySettings,
} from '@shared/accessibility/types'
import { DEFAULT_ACCESSIBILITY_SETTINGS } from '@shared/accessibility/types'

const PROFILE_UPLOAD_SCOPE = '_profile'

export async function loadMyProfile(): Promise<UserProfile> {
  return fetchMe()
}

export async function deleteMyAccount(): Promise<void> {
  await deleteMyAccountApi()
}

export async function saveProfileDetails(input: {
  givenName: string
  familyName: string
  title?: string
}): Promise<UserProfile> {
  const displayName = `${input.givenName.trim()} ${input.familyName.trim()}`.trim()
  return updateMeProfile({
    givenName: input.givenName.trim(),
    familyName: input.familyName.trim(),
    displayName,
    ...(input.title !== undefined ? { title: input.title.trim() } : {}),
  })
}

export async function uploadProfilePhoto(file: File): Promise<UserProfile> {
  const contentType = file.type || 'image/jpeg'
  const presigned = await getAttachmentUploadUrl({
    conversationId: PROFILE_UPLOAD_SCOPE,
    fileName: file.name || 'avatar.jpg',
    contentType,
    attachmentType: 'IMAGE',
  })
  await uploadFileToS3(presigned.uploadUrl, file, contentType)
  return updateMeProfile({ avatarUrl: presigned.attachmentUrl })
}

/**
 * Module-level TTL cache for resolved avatar display URLs.
 *
 * `AvatarImage` resolves its URL in an effect on every mount, and for S3 avatars
 * that means a presigned-download round-trip *per avatar, per mount* — so every
 * list scroll / navigation re-fetched the same URLs with zero reuse. This cache
 * makes resolution instant after the first load and de-dupes concurrent requests
 * for the same key so a freshly-rendered list never fans out into a request storm.
 *
 * Key: the raw stored avatar URL (the S3 url/key). Presigned download URLs are
 * valid for several minutes, so we cache the resolved URL with a conservative TTL
 * well under the presign expiry and re-resolve once it lapses.
 */
const AVATAR_URL_TTL_MS = 4 * 60_000

type CachedAvatarUrl = { url: string; expiresAt: number }

const avatarUrlCache = new Map<string, CachedAvatarUrl>()
const avatarUrlInFlight = new Map<string, Promise<string | null>>()

function isPrivateS3Avatar(avatarUrl: string): boolean {
  return avatarUrl.includes('amazonaws.com')
}

/**
 * Synchronous read of an already-resolved avatar URL so callers (e.g.
 * `AvatarImage`) can paint immediately on mount instead of flashing a fallback
 * while an effect resolves the URL again.
 *
 * Returns:
 * - `null`  → there is definitively no image (no avatar URL set), show fallback.
 * - a string → a ready-to-use display URL (stable non-S3 URL, or a fresh cached
 *   presigned URL).
 * - `undefined` → unknown; an async `resolveAvatarDisplayUrl` is required.
 */
export function getCachedAvatarDisplayUrl(
  avatarUrl?: string | null,
): string | null | undefined {
  if (!avatarUrl) return null
  if (!isPrivateS3Avatar(avatarUrl)) return avatarUrl
  const cached = avatarUrlCache.get(avatarUrl)
  if (cached && cached.expiresAt > Date.now()) return cached.url
  return undefined
}

/**
 * Drop a cached resolution after an `<img onError>` so the fallback initials
 * aren't stuck behind a dead presigned URL for the rest of the TTL window.
 */
export function invalidateAvatarDisplayUrl(avatarUrl?: string | null): void {
  if (!avatarUrl) return
  avatarUrlCache.delete(avatarUrl)
}

export async function resolveAvatarDisplayUrl(
  avatarUrl?: string | null,
): Promise<string | null> {
  if (!avatarUrl) return null
  // Stable (CloudFront / external) URLs need no presigning — return as-is.
  if (!isPrivateS3Avatar(avatarUrl)) return avatarUrl

  const cached = avatarUrlCache.get(avatarUrl)
  if (cached && cached.expiresAt > Date.now()) return cached.url

  // De-dupe concurrent resolutions of the same key (e.g. the same provider's
  // avatar rendered across many rows at once).
  const pending = avatarUrlInFlight.get(avatarUrl)
  if (pending) return pending

  const request = (async () => {
    try {
      const { downloadUrl } = await getAttachmentDownloadUrl(avatarUrl)
      avatarUrlCache.set(avatarUrl, {
        url: downloadUrl,
        expiresAt: Date.now() + AVATAR_URL_TTL_MS,
      })
      return downloadUrl
    } catch {
      // Don't cache failures so a later mount can retry.
      return null
    } finally {
      avatarUrlInFlight.delete(avatarUrl)
    }
  })()

  avatarUrlInFlight.set(avatarUrl, request)
  return request
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  if (newPassword.length < 8) {
    throw new Error('New password must be at least 8 characters')
  }
  await updatePassword({ oldPassword: currentPassword, newPassword })
  // 20A: password change clears soft-lock clocks so every device re-verifies.
  try {
    const { clearSessionSecurityClocks } = await import('./biometric-auth-service')
    clearSessionSecurityClocks()
  } catch {
    /* non-fatal */
  }
}

export async function requestEmailChange(newEmail: string): Promise<void> {
  const trimmed = newEmail.trim().toLowerCase()
  if (!trimmed.includes('@')) {
    throw new Error('Enter a valid email address')
  }
  await updateUserAttribute({
    userAttribute: { attributeKey: 'email', value: trimmed },
  })
}

export type NotificationSettings = {
  /** Play in-app chime when a new message arrives. */
  messageChime?: boolean
  /** Play ringtone for incoming voice calls. */
  voiceCallRingtone?: boolean
  /** Play ringtone for incoming video calls (uses voice call ringtone). */
  videoCallRingtone?: boolean
  /** Receive mobile push for new messages. */
  pushMessages?: boolean
  /** Receive mobile push for incoming / missed calls. */
  pushCalls?: boolean
}

const NOTIFICATION_CACHE_KEY = 'messenger-notification-settings'

const DEFAULT_NOTIFICATION_SETTINGS: Required<NotificationSettings> = {
  messageChime: true,
  voiceCallRingtone: true,
  videoCallRingtone: true,
  pushMessages: true,
  pushCalls: true,
}

function parseSettingsRoot(profile: UserProfile): Record<string, unknown> {
  return parseSettingsJsonRoot(profile.settingsJson)
}

function parseSettingsJsonRoot(settingsJson: unknown): Record<string, unknown> {
  if (!settingsJson) return {}
  try {
    return typeof settingsJson === 'string'
      ? (JSON.parse(settingsJson) as Record<string, unknown>)
      : (settingsJson as Record<string, unknown>)
  } catch {
    return {}
  }
}

export function getNotificationSettings(profile?: UserProfile | null): NotificationSettings {
  if (profile) {
    const notifications = parseSettingsRoot(profile).notifications as NotificationSettings | undefined
    return { ...DEFAULT_NOTIFICATION_SETTINGS, ...notifications }
  }
  if (typeof window === 'undefined') return DEFAULT_NOTIFICATION_SETTINGS
  try {
    const raw = localStorage.getItem(NOTIFICATION_CACHE_KEY)
    if (!raw) return DEFAULT_NOTIFICATION_SETTINGS
    return { ...DEFAULT_NOTIFICATION_SETTINGS, ...(JSON.parse(raw) as NotificationSettings) }
  } catch {
    return DEFAULT_NOTIFICATION_SETTINGS
  }
}

export function cacheNotificationSettings(settings: NotificationSettings): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(NOTIFICATION_CACHE_KEY, JSON.stringify(settings))
}

export async function saveNotificationSettings(
  update: Partial<NotificationSettings>,
): Promise<UserProfile> {
  const current = await loadMyProfile()
  const existing = parseSettingsRoot(current)
  const notifications = (existing.notifications as NotificationSettings | undefined) ?? {}
  const merged = { ...DEFAULT_NOTIFICATION_SETTINGS, ...notifications, ...update }
  const result = await updateMeProfile({
    settingsJson: {
      ...existing,
      notifications: merged,
    },
  })
  cacheNotificationSettings(merged)
  return result
}

export async function saveProviderActivityStatus(
  status: ProviderActivityStatus,
): Promise<UserProfile> {
  const current = await loadMyProfile()
  const existing = parseSettingsRoot(current)
  const profile = await updateMeProfile({
    settingsJson: {
      ...existing,
      activityStatus: status,
    },
  })
  // Reflect the change on the user's own avatar everywhere immediately (the
  // directory poll would otherwise lag by up to a minute). Carry `expiresAt` so
  // the presence store can auto-clear the glyph the moment the status lapses.
  setUserActivityStatus(profile.userId, {
    emoji: status.emoji,
    label: status.label,
    expiresAt: status.expiresAt ?? null,
  })
  return profile
}

export async function clearProviderActivityStatus(): Promise<UserProfile> {
  const current = await loadMyProfile()
  const existing = parseSettingsRoot(current)
  const { activityStatus: _removed, ...rest } = existing
  const profile = await updateMeProfile({ settingsJson: rest })
  setUserActivityStatus(profile.userId, null)
  return profile
}

export async function saveOnCallBookings(periods: OnCallPeriod[]): Promise<UserProfile> {
  const current = await loadMyProfile()
  const existing = parseSettingsRoot(current)
  return updateMeProfile({
    settingsJson: {
      ...existing,
      onCallBookings: periods,
    },
  })
}

export async function saveOnCallOrgConfig(config: OnCallOrgConfig): Promise<UserProfile> {
  const current = await loadMyProfile()
  return adminUpdateUserSettings({
    userId: current.userId,
    settingsPatch: {
      onCallOrgConfig: config,
    },
  })
}

export async function saveDaytimeBookings(
  periods: DaytimeContactPeriod[],
): Promise<UserProfile> {
  const current = await loadMyProfile()
  const existing = parseSettingsRoot(current)
  return updateMeProfile({
    settingsJson: {
      ...existing,
      [DAYTIME_BOOKINGS_KEY]: periods,
    },
  })
}

export async function saveDaytimeOrgConfig(config: DaytimeOrgConfig): Promise<UserProfile> {
  const current = await loadMyProfile()
  return adminUpdateUserSettings({
    userId: current.userId,
    settingsPatch: {
      [DAYTIME_ORG_CONFIG_KEY]: config,
    },
  })
}

export function getAccessibilitySettings(
  profile?: { settingsJson?: unknown } | null,
): AccessibilitySettings {
  if (!profile) return DEFAULT_ACCESSIBILITY_SETTINGS
  const stored = parseSettingsJsonRoot(profile.settingsJson).accessibility as
    | AccessibilitySettings
    | undefined
  return { ...DEFAULT_ACCESSIBILITY_SETTINGS, ...stored }
}

export async function saveAccessibilitySettings(
  update: Partial<AccessibilitySettings> & { preset?: AccessibilityPresetId },
): Promise<UserProfile> {
  const current = await loadMyProfile()
  const existing = parseSettingsRoot(current)
  const accessibility = (existing.accessibility as AccessibilitySettings | undefined) ?? {}
  const merged = { ...DEFAULT_ACCESSIBILITY_SETTINGS, ...accessibility, ...update }
  return updateMeProfile({
    settingsJson: {
      ...existing,
      accessibility: merged,
    },
  })
}
